<template>
  <section class="section pt-5">
    <div class="container">
      <!-- Encabezado -->
      <div class="level mb-5">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title">{{ $t("helpdesk_reports") }}</h1>
              <p class="subtitle">
                {{ $t("analytics_and_performance_metrics") }}
              </p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <div class="field has-addons">
              <div class="control">
                <input class="input" type="date" v-model="date_from" />
              </div>
              <div class="control">
                <input class="input" type="date" v-model="date_to" />
              </div>
              <div class="control">
                <button class="button is-primary" @click="load_reports">
                  <span class="icon">
                    <i class="fa fa-search"></i>
                  </span>
                  <span>{{ $t("filter") }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Estadísticas Generales -->
      <div class="columns mb-5">
        <div class="column">
          <div class="box has-text-centered">
            <p class="heading">{{ $t("total_tickets") }}</p>
            <p class="title has-text-primary">{{ stats.total_tickets || 0 }}</p>
          </div>
        </div>
        <div class="column">
          <div class="box has-text-centered">
            <p class="heading">{{ $t("resolved_tickets") }}</p>
            <p class="title has-text-success">
              {{ stats.resolved_tickets || 0 }}
            </p>
          </div>
        </div>
        <div class="column">
          <div class="box has-text-centered">
            <p class="heading">{{ $t("avg_resolution_time") }}</p>
            <p class="title has-text-info">
              {{ format_hours(stats.avg_resolution_time) }}
            </p>
          </div>
        </div>
        <div class="column">
          <div class="box has-text-centered">
            <p class="heading">{{ $t("satisfaction_rate") }}</p>
            <p class="title has-text-warning">
              {{ format_percentage(stats.satisfaction_rate) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Gráficos y Reportes -->
      <div class="columns">
        <!-- Tickets por Estado -->
        <div class="column is-6">
          <div class="box">
            <h4 class="title is-4">{{ $t("tickets_by_status") }}</h4>
            <div class="content">
              <div
                v-for="status in reports.by_status"
                :key="status.status_id"
                class="level mb-2"
              >
                <div class="level-left">
                  <div class="level-item">
                    <span
                      class="tag"
                      :style="`background-color: ${status.color}; color: white;`"
                    >
                      {{ status.status_name }}
                    </span>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <strong>{{ status.ticket_count }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tickets por Prioridad -->
        <div class="column is-6">
          <div class="box">
            <h4 class="title is-4">{{ $t("tickets_by_priority") }}</h4>
            <div class="content">
              <div
                v-for="priority in reports.by_priority"
                :key="priority.priority_id"
                class="level mb-2"
              >
                <div class="level-left">
                  <div class="level-item">
                    <span
                      class="tag"
                      :style="`background-color: ${priority.color}; color: white;`"
                    >
                      {{ priority.priority_name }}
                    </span>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <strong>{{ priority.ticket_count }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="columns">
        <!-- Tickets por Categoría -->
        <div class="column is-6">
          <div class="box">
            <h4 class="title is-4">{{ $t("tickets_by_category") }}</h4>
            <div class="content">
              <div
                v-for="category in reports.by_category"
                :key="category.category_id"
                class="level mb-2"
              >
                <div class="level-left">
                  <div class="level-item">
                    <span
                      class="tag"
                      :style="`background-color: ${category.color}; color: white;`"
                    >
                      {{ category.category_name }}
                    </span>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <strong>{{ category.ticket_count }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Rendimiento de Agentes -->
        <div class="column is-6">
          <div class="box">
            <h4 class="title is-4">{{ $t("agent_performance") }}</h4>
            <div class="content">
              <div
                v-for="agent in reports.by_agent"
                :key="agent.agent_id"
                class="mb-3"
              >
                <div class="level mb-1">
                  <div class="level-left">
                    <div class="level-item">
                      <strong>{{ agent.agent_name }}</strong>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item">
                      <span class="tag is-primary"
                        >{{ agent.ticket_count }} tickets</span
                      >
                    </div>
                  </div>
                </div>
                <div class="level">
                  <div class="level-left">
                    <div class="level-item">
                      <small
                        >{{ $t("avg_resolution") }}:
                        {{ format_hours(agent.avg_resolution_time) }}</small
                      >
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item">
                      <small
                        >{{ $t("resolved") }}: {{ agent.resolved_count }}</small
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tendencias por Tiempo -->
      <div class="box">
        <h4 class="title is-4">{{ $t("ticket_trends") }}</h4>
        <div class="content">
          <div class="table-container">
            <table class="table is-fullwidth is-striped">
              <thead>
                <tr>
                  <th>{{ $t("date") }}</th>
                  <th>{{ $t("created") }}</th>
                  <th>{{ $t("resolved") }}</th>
                  <th>{{ $t("pending") }}</th>
                  <th>{{ $t("avg_resolution_time") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="trend in reports.trends" :key="trend.date">
                  <td>{{ format_date(trend.date) }}</td>
                  <td>
                    <span class="tag is-info">{{ trend.created_count }}</span>
                  </td>
                  <td>
                    <span class="tag is-success">{{
                      trend.resolved_count
                    }}</span>
                  </td>
                  <td>
                    <span class="tag is-warning">{{
                      trend.pending_count
                    }}</span>
                  </td>
                  <td>{{ format_hours(trend.avg_resolution_time) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- SLA Compliance -->
      <div class="box">
        <h4 class="title is-4">{{ $t("sla_compliance") }}</h4>
        <div class="columns">
          <div class="column">
            <div class="content">
              <div class="level">
                <div class="level-left">
                  <div class="level-item">
                    <p>{{ $t("tickets_within_sla") }}</p>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <span class="tag is-success is-large">
                      {{ stats.sla_compliance_rate }}%
                    </span>
                  </div>
                </div>
              </div>

              <div class="level">
                <div class="level-left">
                  <div class="level-item">
                    <p>{{ $t("overdue_tickets") }}</p>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <span class="tag is-danger is-large">
                      {{ stats.overdue_tickets || 0 }}
                    </span>
                  </div>
                </div>
              </div>

              <progress
                class="progress is-success"
                :value="stats.sla_compliance_rate"
                max="100"
              >
                {{ stats.sla_compliance_rate }}%
              </progress>
            </div>
          </div>
        </div>
      </div>

      <!-- Acciones de Exportación -->
      <div class="box">
        <h4 class="title is-4">{{ $t("export_reports") }}</h4>
        <div class="field is-grouped">
          <div class="control">
            <button class="button is-info" @click="export_pdf">
              <span class="icon">
                <i class="fa fa-file-pdf-o"></i>
              </span>
              <span>{{ $t("export_pdf") }}</span>
            </button>
          </div>
          <div class="control">
            <button class="button is-success" @click="export_excel">
              <span class="icon">
                <i class="fa fa-file-excel-o"></i>
              </span>
              <span>{{ $t("export_excel") }}</span>
            </button>
          </div>
          <div class="control">
            <button class="button is-primary" @click="schedule_report">
              <span class="icon">
                <i class="fa fa-clock-o"></i>
              </span>
              <span>{{ $t("schedule_report") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { get, format_date } from "@/utils";

export default {
  name: "HelpdeskReports",
  data() {
    return {
      date_from: "",
      date_to: "",
      stats: {},
      reports: {
        by_status: [],
        by_priority: [],
        by_category: [],
        by_agent: [],
        trends: [],
      },
      loading: false,
    };
  },
  methods: {
    load_reports() {
      this.loading = true;

      const params = {};
      if (this.date_from) params.date_from = this.date_from;
      if (this.date_to) params.date_to = this.date_to;

      // Cargar estadísticas generales
      get("/helpdesk/ticket/reports/stats", params)
        .then((data) => {
          this.stats = data;
        })
        .catch((error) => {
          console.error("Error loading stats:", error);
        });

      // Cargar reportes por categorías
      get("/helpdesk/ticket/reports/by-status", params)
        .then((data) => {
          this.reports.by_status = data;
        })
        .catch((error) => {
          console.error("Error loading status reports:", error);
        });

      get("/helpdesk/ticket/reports/by-priority", params)
        .then((data) => {
          this.reports.by_priority = data;
        })
        .catch((error) => {
          console.error("Error loading priority reports:", error);
        });

      get("/helpdesk/ticket/reports/by-category", params)
        .then((data) => {
          this.reports.by_category = data;
        })
        .catch((error) => {
          console.error("Error loading category reports:", error);
        });

      get("/helpdesk/ticket/reports/by-agent", params)
        .then((data) => {
          this.reports.by_agent = data;
        })
        .catch((error) => {
          console.error("Error loading agent reports:", error);
        });

      get("/helpdesk/ticket/reports/trends", params)
        .then((data) => {
          this.reports.trends = data;
        })
        .catch((error) => {
          console.error("Error loading trends:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    format_date(date) {
      return format_date(date);
    },

    format_hours(hours) {
      if (!hours) return "0h";
      if (hours < 1) return `${Math.round(hours * 60)}min`;
      return `${Math.round(hours * 10) / 10}h`;
    },

    format_percentage(rate) {
      if (!rate) return "0%";
      return `${Math.round(rate)}%`;
    },

    export_pdf() {
      const params = {};
      if (this.date_from) params.date_from = this.date_from;
      if (this.date_to) params.date_to = this.date_to;

      window.open(
        `/api/helpdesk/ticket/reports/export/pdf?${new URLSearchParams(
          params
        )}`,
        "_blank"
      );
    },

    export_excel() {
      const params = {};
      if (this.date_from) params.date_from = this.date_from;
      if (this.date_to) params.date_to = this.date_to;

      window.open(
        `/api/helpdesk/ticket/reports/export/excel?${new URLSearchParams(
          params
        )}`,
        "_blank"
      );
    },

    schedule_report() {
      // Implementar modal para programar reportes
      this.$toast.info(this.$t("feature_coming_soon"));
    },

    set_default_dates() {
      const today = new Date();
      const last_month = new Date();
      last_month.setMonth(today.getMonth() - 1);

      this.date_to = today.toISOString().split("T")[0];
      this.date_from = last_month.toISOString().split("T")[0];
    },
  },
  mounted() {
    this.set_default_dates();
    this.load_reports();
  },
};
</script>

<style scoped>
.container {
  max-width: 1400px;
}

.progress {
  height: 1.5rem;
}

.table th {
  border-bottom: 2px solid #dbdbdb;
}

.level {
  margin-bottom: 0.5rem;
}

.box {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
